import type { PhoneRevealSummary, StaffVisibilityView } from "@planerius/contracts/cabinet"
import { type ReactNode, useCallback, useEffect, useState } from "react"

import { useHasPermission } from "../../access/use-access"
import { type ApiProblem, isApiProblem } from "../../api/problem"
import { fetchPhoneReveals, fetchStaffVisibilitySettings, saveStaffVisibility } from "../../api/settings.api"
import { ProblemScreen, Skeleton } from "../../shell/states"
import { useOnline } from "../../shell/use-online"
import { Button } from "../../ui"
import { StandHeader } from "../directories/stand"
import {
  confirmText,
  daysLabel,
  REVEAL_FAILED,
  revealEmptyText,
  revealLine,
  saveFailureText,
  toggleText,
  VISIBILITY_TOGGLES,
  type VisibilityToggle,
} from "./staff-visibility-model"
import styles from "./branch-screen.module.css"

/**
 * 4-Б12 «Что мастер видит о клиенте» — /settings/staff-visibility.
 *
 * ЭТО НЕ НАСТРОЙКА ИНТЕРФЕЙСА, А РЕШЕНИЕ О ДОВЕРИИ. Право отвечает «что положено
 * по должности», настройка — «насколько салон доверяет коллективу». Поэтому включение
 * и выключение спрашиваются РАЗНЫМИ словами: включить значит открыть базу шире,
 * выключить — отобрать то, чем мастер уже пользуется.
 *
 * ЖУРНАЛ ОТКРЫТИЙ ЖИВЁТ НА ЭТОМ ЖЕ ЭКРАНЕ. Настройка продаётся как доверие, а проверяется
 * цифрами: кто открывал номера, сколько раз и скольких РАЗНЫХ клиентов. Двадцать открытий
 * одного клиента — работа, двадцать разных подряд — перебор базы глазами.
 *
 * БЕЗ СЕТИ ПЕРЕКЛЮЧАТЕЛИ ЗАБЛОКИРОВАНЫ: настройка, «сохранённая» в офлайне, — это ложное
 * чувство безопасности до первой проверки.
 */
export function StaffVisibilityScreen(): ReactNode {
  const canManage = useHasPermission("roles.manage")
  const canAudit = useHasPermission("audit.view")
  const online = useOnline()

  const [settings, setSettings] = useState<StaffVisibilityView | null>(null)
  const [reveals, setReveals] = useState<PhoneRevealSummary | null>(null)
  /*
   * ОТКАЗ ЖУРНАЛА — НЕ «ОТКРЫТИЙ НЕ БЫЛО». Пока отказ сливался с `reveals === null`,
   * 500, таймаут и обрыв связи рисовали на экране положительное утверждение о
   * безопасности: «за неделю номера никто не открывал». Единственный экран, ради
   * которого журнал заведён, в сломанном состоянии успокаивал владельца.
   */
  const [revealsFailed, setRevealsFailed] = useState(false)
  const [loadFailed, setLoadFailed] = useState<ApiProblem | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [asking, setAsking] = useState<{ toggle: VisibilityToggle; next: boolean } | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const next = await fetchStaffVisibilitySettings(signal)
      if (signal?.aborted === true) return
      setSettings(next)
      setLoadFailed(null)
    } catch (error) {
      if (signal?.aborted === true) return
      if (!isApiProblem(error)) throw error
      setLoadFailed(error)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  useEffect(() => {
    if (!canAudit) return
    const controller = new AbortController()
    void (async () => {
      try {
        const summary = await fetchPhoneReveals(7, controller.signal)
        if (!controller.signal.aborted) {
          setReveals(summary)
          setRevealsFailed(false)
        }
      } catch {
        // Журнал открытий — справка рядом с настройкой, а не условие её работы:
        // экран продолжает работать. Но о том, что справки нет, он говорит вслух.
        if (!controller.signal.aborted) {
          setReveals(null)
          setRevealsFailed(true)
        }
      }
    })()
    return () => controller.abort()
  }, [canAudit])

  if (loadFailed !== null && settings === null) {
    return <ProblemScreen problem={loadFailed} onRetry={() => void load()} />
  }

  const apply = async (toggle: VisibilityToggle, next: boolean): Promise<void> => {
    if (settings === null) return
    setBusy(true)
    setFailure(null)
    try {
      // Новое состояние приходит ТЕМ ЖЕ ответом, что и запись. Раньше экран узнавал его
      // вторым запросом, и сорвавшееся перечитывание оставляло на экране прежнее
      // значение без единого слова: салон считал, что номера закрыты, а мастера уже
      // видели телефоны всей базы.
      const saved = await saveStaffVisibility({
        showClientPhoneToMasters:
          toggle.field === "showClientPhoneToMasters" ? next : settings.showClientPhoneToMasters,
        showMoneyToMasters:
          toggle.field === "showMoneyToMasters" ? next : settings.showMoneyToMasters,
        expectedUpdatedAt: settings.updatedAt,
      })
      setSettings(saved)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      // ПРЕЖНЕЕ ЗНАЧЕНИЕ ПРОДОЛЖАЕТ ДЕЙСТВОВАТЬ — это и есть главное, что надо сказать:
      // человек должен знать, открыты сейчас номера или закрыты, а не гадать.
      setFailure(saveFailureText(error.detail, error.code))
      if (error.code === "version_conflict") await load()
    } finally {
      setBusy(false)
      setAsking(null)
    }
  }

  return (
    <div className={styles["screen"]}>
      <StandHeader title="Что мастер видит о клиенте" />

      {settings === null ? (
        <div className={styles["skeletons"]}>
          {[0, 1].map((row) => (
            <Skeleton key={row} width="100%" height={64} radius={10} />
          ))}
        </div>
      ) : (
        <div className={styles["column"]}>
          {VISIBILITY_TOGGLES.map((toggle) => {
            const value = settings[toggle.field]
            return (
              <div key={toggle.field} className={styles["block"]}>
                <div className={styles["blockTitle"]}>{toggle.title}</div>
                <p className={styles["consequence"]}>{toggleText(toggle, value)}</p>
                <div className={styles["chips"]}>
                  <Button
                    size="sm"
                    variant={value ? "primary" : "secondary"}
                    loading={busy && asking?.toggle.field === toggle.field}
                    loadingLabel="Сохраняем…"
                    deniedReason={
                      !canManage
                        ? "Настройку доверия меняет владелец салона — управляющий видит её только на чтение"
                        : online
                          ? undefined
                          : "Пока нет связи, настройку менять нельзя: «сохранённая» в офлайне, она даёт ложное чувство безопасности"
                    }
                    onClick={() => {
                      setAsking({ toggle, next: !value })
                    }}
                  >
                    {value ? "Выключить" : "Включить"}
                  </Button>
                </div>
              </div>
            )
          })}

          {asking === null ? null : (
            <div className={styles["conflict"]} role="alert">
              <span>{confirmText(asking.toggle, asking.next)}</span>
              <div className={styles["conflictButtons"]}>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  loadingLabel="Сохраняем…"
                  onClick={() => {
                    void apply(asking.toggle, asking.next)
                  }}
                >
                  {asking.next ? "Включить" : "Выключить"}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setAsking(null)
                  }}
                >
                  Оставить как есть
                </Button>
              </div>
            </div>
          )}

          {failure === null ? null : (
            <div className={styles["error"]} role="alert">
              {failure}
            </div>
          )}

          {!canAudit ? null : (
            <div className={styles["block"]}>
              <div className={styles["blockTitle"]}>
                Журнал открытий телефона · {daysLabel(reveals?.days ?? 7)}
              </div>
              {revealsFailed ? (
                <p className={styles["error"]} role="alert">
                  {REVEAL_FAILED}
                </p>
              ) : reveals === null ? (
                <Skeleton width="100%" height={48} radius={10} />
              ) : reveals.rows.length === 0 ? (
                <p className={styles["hint"]}>{revealEmptyText(reveals.days)}</p>
              ) : (
                <div className={styles["reveals"]}>
                  {reveals.rows.map((row) => (
                    <div key={row.memberId ?? row.displayName} className={styles["revealRow"]}>
                      <span>{row.displayName}</span>
                      <span className={styles["hint"]}>{revealLine(row)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
